"use client";

import {
  AlertCircle,
  CheckCircle2,
  Compass,
  Lightbulb,
  Loader2,
  Mail,
  Rocket,
  X,
  Zap,
} from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import { useEmailWallAnalytics } from "./use-email-wall-analytics";
import { useNewsletterSubscribe } from "./use-newsletter-subscribe";
import { useSubscriptionStatus } from "./use-subscription-status";

type OverlayVariant = "center" | "bottom";
type FormSurface = "inline" | "overlay-center" | "overlay-bottom";

const ARTICLE_SCROLL_THRESHOLD = 0.7;
const DISMISS_STORAGE_KEY = "email-wall-dismissed-v1";
const EVER_DISMISSED_STORAGE_KEY = "email-wall-ever-dismissed-v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function readDismissedFromStorage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const dismissedAt = Number.parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) {
      window.localStorage.removeItem(DISMISS_STORAGE_KEY);
      return false;
    }
    if (Date.now() - dismissedAt > DISMISS_TTL_MS) {
      window.localStorage.removeItem(DISMISS_STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function readEverDismissedFromStorage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(EVER_DISMISSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissedToStorage(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    window.localStorage.setItem(EVER_DISMISSED_STORAGE_KEY, "1");
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

const EDITION_COPY = {
  engineer: {
    headline: "The AI shipping log.\nEvery morning.",
    compactHeadline: "Get the latest AI breakthrough\nin your inbox",
    subtitle:
      "5 stories + 30 briefs daily. New tools, models, and workflows your dev stack actually needs.",
    features: [
      { icon: Zap, text: "New tools daily" },
      { icon: Lightbulb, text: "Skills & prompts" },
      { icon: Rocket, text: "Top workflows" },
      { icon: Compass, text: "Early signal" },
    ],
    cta: "Subscribe for free",
    socialProof: "engineers and technical founders",
  },
  creative: {
    headline: "What AI made yesterday.\nEvery morning.",
    compactHeadline: "Get the latest AI breakthrough\nin your inbox",
    subtitle:
      "5 stories + 30 briefs daily. New generators, workflows, and prompts your creative work actually needs.",
    features: [
      { icon: Zap, text: "New tools daily" },
      { icon: Lightbulb, text: "Skills & prompts" },
      { icon: Rocket, text: "Top workflows" },
      { icon: Compass, text: "Early signal" },
    ],
    cta: "Subscribe for free",
    socialProof: "designers and creative professionals",
  },
} satisfies Record<
  ReaderReportType,
  {
    headline: string;
    compactHeadline: string;
    subtitle: string;
    features: { icon: typeof Zap; text: string }[];
    cta: string;
    socialProof: string;
  }
>;

const CROSS_SELL_COPY = {
  engineer: {
    prompt: "Also cover AI creative tools?",
    cta: "Yes, add Creative edition",
  },
  creative: {
    prompt: "Also cover AI engineering?",
    cta: "Yes, add Engineer edition",
  },
} satisfies Record<ReaderReportType, { prompt: string; cta: string }>;

const CONFETTI_COLORS = [
  "var(--emailwall-success)",
  "#86efac",
  "#4ade80",
  "#bbf7d0",
  "var(--explore-accent-yellow)",
];

function ConfettiBurst() {
  const pieces = Array.from({ length: 14 }, (_, i) => {
    const angle = (Math.PI * 2 * i) / 14 + Math.random() * 0.3;
    const distance = 90 + Math.random() * 80;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance - 30;
    const rot = 240 + Math.random() * 480;
    const duration = 1.0 + Math.random() * 0.6;
    const color =
      CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
    const delay = Math.random() * 0.06;
    return (
      <span
        className="emailwall-confetti-piece"
        key={`confetti-${String(i)}`}
        style={
          {
            backgroundColor: color,
            animationDelay: `${String(delay)}s`,
            "--confetti-x": `${String(x)}px`,
            "--confetti-y": `${String(y)}px`,
            "--confetti-rot": `${String(rot)}deg`,
            "--confetti-duration": `${String(duration)}s`,
          } as React.CSSProperties
        }
      />
    );
  });

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      {pieces}
    </div>
  );
}

function SubscribeForm({
  copy,
  email,
  setEmail,
  isSubmitting,
  isError,
  errorMessage,
  onSubmit,
  surface,
  errorId,
}: {
  copy: (typeof EDITION_COPY)[ReaderReportType];
  email: string;
  setEmail: (v: string) => void;
  isSubmitting: boolean;
  isError: boolean;
  errorMessage?: string;
  onSubmit: (e: React.FormEvent) => void;
  surface: FormSurface;
  errorId: string;
}) {
  const isInline = surface === "inline";
  const isBottom = surface === "overlay-bottom";

  // Bottom uses horizontal layout with compact copy
  if (isBottom) {
    return (
      <>
        <h3
          className="mb-2 whitespace-pre-line font-bold text-[1.4rem] text-[var(--explore-on-accent-text)] leading-[1.15] tracking-[-0.015em] sm:mb-2 sm:whitespace-normal sm:text-[1.85rem] sm:leading-[1.1] lg:text-[2rem]"
          style={{ fontFamily: "var(--reader-title-font-stack)" }}
        >
          {copy.compactHeadline}
        </h3>
        <p className="mb-4 text-[0.95rem] text-[var(--emailwall-on-accent-secondary)] leading-[1.4] sm:mb-4 sm:text-[1rem]">
          <span className="font-semibold text-[var(--explore-on-accent-text)]">
            5 stories + 30 briefs
          </span>{" "}
          daily, from 500+ sources. 5-minute read for {copy.socialProof}.
        </p>

        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3"
          onSubmit={onSubmit}
        >
          <input
            aria-describedby={isError ? errorId : undefined}
            aria-invalid={isError}
            aria-label="Email address"
            className="h-12 w-full rounded-xl border border-[var(--emailwall-on-accent-border)] bg-[var(--emailwall-input-bg)] px-5 text-[1rem] text-[var(--explore-on-accent-text)] shadow-[var(--emailwall-shadow-input)] outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-[var(--emailwall-on-accent-placeholder)] focus:border-[var(--explore-on-accent-text)] focus:ring-4 focus:ring-[var(--explore-on-accent-text)]/15 focus-visible:border-[var(--explore-on-accent-text)] sm:h-[52px] sm:w-[340px] sm:flex-none sm:text-[1rem]"
            disabled={isSubmitting}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            type="email"
            value={email}
          />
          <button
            aria-busy={isSubmitting}
            className="hover:-translate-y-[1px] flex h-12 w-full animate-emailwall-btn items-center justify-center gap-2.5 whitespace-nowrap rounded-xl bg-[var(--explore-on-accent-text)] px-6 font-bold text-[1rem] text-[var(--emailwall-cta-text)] tracking-[0.01em] shadow-[var(--emailwall-shadow-button)] transition-[box-shadow,transform] duration-150 hover:shadow-[var(--emailwall-shadow-button-hover)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--explore-on-accent-text)]/25 active:translate-y-0 disabled:opacity-70 sm:h-[52px] sm:w-auto sm:text-[1.05rem]"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <Loader2 aria-hidden className="h-5 w-5 shrink-0 animate-spin" />
            ) : (
              <Mail aria-hidden className="h-5 w-5 shrink-0" />
            )}
            <span>{isSubmitting ? "Subscribing…" : copy.cta}</span>
          </button>
          <div
            aria-hidden
            className="hidden h-[52px] w-[52px] shrink-0 overflow-hidden rounded-full shadow-[var(--emailwall-shadow-button)] sm:block"
          >
            <Image
              alt=""
              className="h-full w-full object-cover"
              height={52}
              src="/logo.webp"
              width={52}
            />
          </div>
        </form>

        {isError && (
          <p
            aria-live="polite"
            className="mt-2.5 flex items-center gap-1.5 text-[0.85rem] text-[var(--emailwall-error)]"
            id={errorId}
          >
            <AlertCircle
              aria-hidden
              className="h-[18px] w-[18px] shrink-0"
              strokeWidth={2.25}
            />
            <span>{errorMessage}</span>
          </p>
        )}

        <p className="mt-3 text-[0.82rem] text-[var(--emailwall-on-accent-secondary)]">
          Free · no spam · unsubscribe in one click
        </p>
      </>
    );
  }

  // Inline and overlay-center share the expanded layout (title + subtitle + features + form)
  return (
    <>
      <h3
        className={`whitespace-pre-line text-center font-bold text-[var(--explore-on-accent-text)] leading-[1.08] tracking-[-0.02em] ${
          isInline
            ? "mb-4 text-[2rem] sm:text-[2.5rem]"
            : "mb-3 text-[1.75rem] sm:text-[2.1rem]"
        }`}
        style={{ fontFamily: "var(--reader-title-font-stack)" }}
      >
        {copy.headline}
      </h3>

      <p
        className={`mx-auto text-center text-[var(--emailwall-on-accent-secondary)] leading-[1.55] ${
          isInline
            ? "mb-7 max-w-[520px] text-[0.95rem] sm:text-[1rem]"
            : "mb-5 max-w-[440px] text-[0.9rem]"
        }`}
      >
        {copy.subtitle}
      </p>

      <div
        className={`grid grid-cols-2 ${isInline ? "mb-8 gap-2.5" : "mb-5 gap-2"}`}
      >
        {copy.features.map((feature) => (
          <div
            className={`flex items-center gap-2.5 rounded-xl border border-[var(--emailwall-pill-border)] bg-[var(--emailwall-pill-bg)] shadow-[var(--emailwall-shadow-pill)] ${
              isInline ? "px-4 py-3" : "px-3 py-2.5"
            }`}
            key={feature.text}
          >
            <feature.icon
              className={`shrink-0 text-[var(--explore-on-accent-text)] ${isInline ? "h-4 w-4" : "h-3.5 w-3.5"}`}
              strokeWidth={2.25}
            />
            <span
              className={`truncate font-semibold text-[var(--explore-on-accent-text)] leading-snug ${isInline ? "text-[0.85rem]" : "text-[0.78rem]"}`}
            >
              {feature.text}
            </span>
          </div>
        ))}
      </div>

      <form
        className={`flex flex-col gap-2.5 ${isInline ? "mx-auto max-w-[440px]" : ""}`}
        onSubmit={onSubmit}
      >
        <input
          aria-describedby={isError ? errorId : undefined}
          aria-invalid={isError}
          aria-label="Email address"
          className="h-14 w-full rounded-xl border border-[var(--emailwall-on-accent-border)] bg-[var(--emailwall-input-bg)] px-5 text-[1rem] text-[var(--explore-on-accent-text)] shadow-[var(--emailwall-shadow-input)] outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-[var(--emailwall-on-accent-placeholder)] focus:border-[var(--explore-on-accent-text)] focus:ring-4 focus:ring-[var(--explore-on-accent-text)]/15 focus-visible:border-[var(--explore-on-accent-text)]"
          disabled={isSubmitting}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          type="email"
          value={email}
        />
        <button
          aria-busy={isSubmitting}
          className="hover:-translate-y-[1px] flex h-14 w-full animate-emailwall-btn items-center justify-center gap-2 rounded-xl bg-[var(--explore-on-accent-text)] font-bold text-[1.05rem] text-[var(--emailwall-cta-text)] tracking-[0.01em] shadow-[var(--emailwall-shadow-button)] transition-[box-shadow,transform] duration-150 hover:shadow-[var(--emailwall-shadow-button-hover)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--explore-on-accent-text)]/25 active:translate-y-0 disabled:opacity-70"
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? (
            <Loader2 aria-hidden className="h-5 w-5 shrink-0 animate-spin" />
          ) : (
            <Mail aria-hidden className="h-5 w-5 shrink-0" />
          )}
          <span>{isSubmitting ? "Subscribing…" : copy.cta}</span>
        </button>
      </form>

      {isError && (
        <p
          aria-live="polite"
          className="mt-3 flex items-center justify-center gap-1.5 text-[0.85rem] text-[var(--emailwall-error)]"
          id={errorId}
        >
          <AlertCircle
            aria-hidden
            className="h-[18px] w-[18px] shrink-0"
            strokeWidth={2.25}
          />
          <span>{errorMessage}</span>
        </p>
      )}

      <p className="mt-3 text-center text-[0.78rem] text-[var(--emailwall-on-accent-secondary)]">
        Free, daily, for {copy.socialProof}
      </p>
    </>
  );
}

function SuccessState({
  upsellDone,
  upsellSkipped,
  crossSellPrompt,
  crossSellCta,
  onUpsell,
  onSkip,
  upsellSubmitting,
  showUpsell,
  layout,
}: {
  upsellDone: boolean;
  upsellSkipped: boolean;
  crossSellPrompt: string;
  crossSellCta: string;
  onUpsell: () => void;
  onSkip: () => void;
  upsellSubmitting: boolean;
  showUpsell: boolean;
  layout: "center" | "horizontal";
}) {
  const isHorizontal = layout === "horizontal";

  return (
    <div
      className={`relative overflow-visible ${isHorizontal ? "" : "text-center"}`}
    >
      <ConfettiBurst />
      <div className="animate-emailwall-success">
        <div
          className={`mb-3 flex items-center gap-2.5 ${isHorizontal ? "" : "justify-center"}`}
        >
          <CheckCircle2
            className="h-8 w-8 text-[var(--emailwall-success)]"
            strokeWidth={2.25}
          />
          <span
            className="font-bold text-[1.5rem] text-[var(--explore-on-accent-text)] tracking-[-0.01em]"
            style={{ fontFamily: "var(--reader-title-font-stack)" }}
          >
            You're subscribed.
          </span>
        </div>
        <p
          className={`text-[0.95rem] text-[var(--emailwall-on-accent-secondary)] ${isHorizontal ? "mb-5 sm:mb-5" : "mb-5"}`}
        >
          Look for your first digest in your inbox soon.
        </p>

        {showUpsell && !upsellDone && !upsellSkipped && (
          <div
            className={`animate-emailwall-crosssell ${isHorizontal ? "flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4" : ""}`}
          >
            <p
              className={`text-[0.92rem] text-[var(--emailwall-on-accent-secondary)] ${isHorizontal ? "" : "mb-3"}`}
            >
              {crossSellPrompt}
            </p>
            <div
              className={`flex items-center gap-3 ${isHorizontal ? "sm:shrink-0" : "flex-col"}`}
            >
              <button
                aria-busy={upsellSubmitting}
                className={`hover:-translate-y-[1px] flex h-12 items-center justify-center rounded-xl bg-[var(--explore-on-accent-text)] px-6 font-bold text-[0.95rem] text-[var(--emailwall-cta-text)] shadow-[var(--emailwall-shadow-button)] transition-[box-shadow,transform] duration-150 hover:shadow-[var(--emailwall-shadow-button-hover)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--explore-on-accent-text)]/25 active:translate-y-0 disabled:opacity-70 ${isHorizontal ? "" : "w-full"}`}
                disabled={upsellSubmitting}
                onClick={onUpsell}
                type="button"
              >
                {upsellSubmitting && (
                  <Loader2
                    aria-hidden
                    className="mr-2 h-4 w-4 shrink-0 animate-spin"
                  />
                )}
                {upsellSubmitting ? "Adding…" : crossSellCta}
              </button>
              <button
                className="rounded-md border-none bg-transparent px-2 py-1 text-[0.875rem] text-[var(--emailwall-on-accent-secondary)] underline decoration-1 underline-offset-4 transition-colors hover:text-[var(--explore-on-accent-text)] hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--explore-on-accent-text)]/40"
                onClick={onSkip}
                type="button"
              >
                No thanks
              </button>
            </div>
          </div>
        )}

        {upsellDone && (
          <p className="font-semibold text-[0.95rem] text-[var(--explore-on-accent-text)]">
            You're getting both editions.
          </p>
        )}
      </div>
    </div>
  );
}

function getOverlayVariant(): OverlayVariant {
  if (typeof window === "undefined") {
    return "bottom";
  }
  const param = new URLSearchParams(window.location.search).get("emailwall");
  if (param === "bottom" || param === "center") {
    return param;
  }
  return "bottom";
}

function computeArticleScrollProgress(): number {
  if (typeof window === "undefined") {
    return 0;
  }
  const body = document.querySelector<HTMLElement>(".explore-story-body");
  if (!body) {
    return 0;
  }
  const rect = body.getBoundingClientRect();
  const bodyTopAbsolute = rect.top + window.scrollY;
  // Measure how far the reader has actually scrolled through the story body,
  // not how far the viewport bottom has reached. Short stories or tall screens
  // otherwise register >70% progress on first paint, triggering the wall
  // before the reader has scrolled at all.
  const scrollable = Math.max(0, body.offsetHeight - window.innerHeight);
  if (scrollable === 0) {
    return 0;
  }
  const scrolledPast = window.scrollY - bodyTopAbsolute;
  if (scrolledPast <= 0) {
    return 0;
  }
  return Math.min(1, scrolledPast / scrollable);
}

export function EmailWall() {
  const {
    email,
    setEmail,
    subscribe,
    reportType,
    isSubmitting,
    isError,
    state,
    subscribeToEdition,
  } = useNewsletterSubscribe();

  const { subscribedToCurrent, subscribedToOther, otherEdition } =
    useSubscriptionStatus();

  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);

  const [upsellSubmitting, setUpsellSubmitting] = useState(false);
  const [upsellDone, setUpsellDone] = useState(false);
  const [upsellSkipped, setUpsellSkipped] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayDismissed, setOverlayDismissed] = useState(
    readDismissedFromStorage
  );
  const [overlayWasTriggered, setOverlayWasTriggered] = useState(false);
  const [variant, setVariant] = useState<OverlayVariant>("bottom");
  const [devForceActive, setDevForceActive] = useState(false);
  const overlayTriggered = useRef(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const bottomBannerRef = useRef<HTMLDivElement>(null);
  const inlineObserverRef = useRef<IntersectionObserver | null>(null);

  // Analytics timing/tracking refs — not rendered, used for property derivation
  const shownAtRef = useRef<number | null>(null);
  const submitAttemptedAtRef = useRef<number | null>(null);
  const upsellOfferedAtRef = useRef<number | null>(null);
  const dismissMethodRef = useRef<
    "close_button" | "esc" | "backdrop" | "native_close" | null
  >(null);
  const upsellOfferedTrackedRef = useRef(false);
  const inlineViewedTrackedRef = useRef(false);
  const submitSucceededTrackedRef = useRef(false);
  const submitFailedTrackedRef = useRef(false);

  const [hasEverDismissed, setHasEverDismissed] = useState(
    readEverDismissedFromStorage
  );

  const analytics = useEmailWallAnalytics({
    variant,
    edition: reportType,
    pathname,
    isReturningDismisser: hasEverDismissed,
  });
  // Mirror analytics into a ref so effects can read the latest capture
  // helpers without re-subscribing whenever base props change. Written
  // in an effect (not during render) to stay inside React's commit phase.
  const analyticsRef = useRef(analytics);
  useEffect(() => {
    analyticsRef.current = analytics;
  }, [analytics]);

  const dismissTrackedRef = useRef(false);

  // Latch success so the upsell API call (which flips state.status back to
  // "submitting") can't make the form re-appear under the success screen.
  const [hasSubscribedOnce, setHasSubscribedOnce] = useState(false);
  useEffect(() => {
    if (state.status === "success") {
      setHasSubscribedOnce(true);
    }
  }, [state.status]);
  const justSubscribed = hasSubscribedOnce;

  // Fire submit outcome events once per attempt (refs re-armed by handleSubmit).
  useEffect(() => {
    if (state.status === "success" && !submitSucceededTrackedRef.current) {
      submitSucceededTrackedRef.current = true;
      analyticsRef.current.trackSubmitSucceeded({
        already_subscribed: state.alreadySubscribed ?? false,
        time_since_shown_ms:
          shownAtRef.current !== null ? Date.now() - shownAtRef.current : null,
      });
      return;
    }
    if (state.status === "error" && !submitFailedTrackedRef.current) {
      submitFailedTrackedRef.current = true;
      analyticsRef.current.trackSubmitFailed({
        error_message: state.message,
      });
    }
  }, [state]);

  // Per-story state: if the App Router preserves this component across a
  // soft navigation (e.g. story → story), reset the one-shot triggers so
  // the wall re-arms on the new page. 24h dismissal lives in localStorage
  // and is re-read here.
  useEffect(() => {
    if (pathname === lastPathnameRef.current) {
      return;
    }
    lastPathnameRef.current = pathname;
    overlayTriggered.current = false;
    setShowOverlay(false);
    setOverlayWasTriggered(false);
    setHasSubscribedOnce(false);
    setUpsellDone(false);
    setUpsellSkipped(false);
    setUpsellSubmitting(false);
    setOverlayDismissed(readDismissedFromStorage());
    setHasEverDismissed(readEverDismissedFromStorage());
    shownAtRef.current = null;
    submitAttemptedAtRef.current = null;
    upsellOfferedAtRef.current = null;
    dismissMethodRef.current = null;
    upsellOfferedTrackedRef.current = false;
    inlineViewedTrackedRef.current = false;
    submitSucceededTrackedRef.current = false;
    submitFailedTrackedRef.current = false;
    dismissTrackedRef.current = false;
  }, [pathname]);

  useEffect(() => {
    setVariant(getOverlayVariant());
  }, []);

  // Dev helper: `?_resetsub=1` clears the subscription cookie so the scroll
  // trigger can be tested without manually deleting cookies in DevTools.
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("_resetsub") !== "1") {
      return;
    }
    // biome-ignore lint/suspicious/noDocumentCookie: dev-only reset for testing the scroll trigger
    document.cookie =
      "ai-primer-subscribed-v2=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    params.delete("_resetsub");
    const url = new URL(window.location.href);
    url.search = params.toString();
    window.location.replace(url.toString());
  }, []);

  useEffect(() => {
    if (overlayTriggered.current) {
      return;
    }

    const devForce =
      process.env.NODE_ENV === "development" &&
      new URLSearchParams(window.location.search).get("_emailwall") === "1";

    // Dev force bypasses ALL guards
    if (devForce) {
      overlayTriggered.current = true;
      setDevForceActive(true);
      setOverlayWasTriggered(true);
      setShowOverlay(true);
      shownAtRef.current = Date.now();
      analyticsRef.current.trackShown({
        trigger: "dev_force",
        scroll_progress: computeArticleScrollProgress(),
      });
      return;
    }

    if (subscribedToCurrent) {
      return;
    }

    // If the user dismissed within the 24h TTL, don't flip overlayWasTriggered —
    // that would suppress the inline footer wall and leave no signup surface
    // on the next story until the TTL expires. Let the inline wall render.
    if (overlayDismissed) {
      return;
    }

    const trigger = () => {
      if (overlayTriggered.current) {
        return;
      }
      overlayTriggered.current = true;
      setOverlayWasTriggered(true);
      setShowOverlay(true);
      shownAtRef.current = Date.now();
      analyticsRef.current.trackShown({
        trigger: "scroll",
        scroll_progress: computeArticleScrollProgress(),
      });
    };

    let rafId: number | null = null;
    const evaluate = () => {
      rafId = null;
      if (overlayTriggered.current) {
        return;
      }
      if (computeArticleScrollProgress() >= ARTICLE_SCROLL_THRESHOLD) {
        trigger();
      }
    };
    const handleScroll = () => {
      if (overlayTriggered.current || rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(evaluate);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [subscribedToCurrent, overlayDismissed]);

  const handleOverlayDismiss = useCallback(() => {
    // Only track as a "dismiss" if the user hadn't subscribed — the same
    // handler auto-closes the surface after the upsell completes, and that
    // isn't a dismissal.
    if (!(dismissTrackedRef.current || hasSubscribedOnce)) {
      dismissTrackedRef.current = true;
      analyticsRef.current.trackDismissed({
        method: dismissMethodRef.current ?? "native_close",
        time_since_shown_ms:
          shownAtRef.current !== null ? Date.now() - shownAtRef.current : null,
      });
    }
    setOverlayDismissed(true);
    setHasEverDismissed(true);
    writeDismissedToStorage();
  }, [hasSubscribedOnce]);

  const handleOverlayUpsellSkip = useCallback(() => {
    analyticsRef.current.trackUpsellSkipped({
      other_edition: otherEdition,
      surface: "overlay",
    });
    handleOverlayDismiss();
  }, [otherEdition, handleOverlayDismiss]);

  // After the upsell confirmation lands, close the banner shortly — the user
  // has completed the flow so we get out of the way. Before the user resolves
  // the cross-sell (accept or skip), the banner stays put.
  useEffect(() => {
    if (!upsellDone || overlayDismissed) {
      return;
    }
    const timer = setTimeout(handleOverlayDismiss, 1500);
    return () => clearTimeout(timer);
  }, [upsellDone, overlayDismissed, handleOverlayDismiss]);

  // Use native <dialog> for center variant (focus trap + ESC for free)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const shouldShow = showOverlay && !overlayDismissed && variant === "center";
    if (shouldShow && !dialog.open) {
      dialog.showModal();
    } else if (!shouldShow && dialog.open) {
      dialog.close();
    }
  }, [showOverlay, overlayDismissed, variant]);

  // Bottom banner is a non-modal <div role="dialog">, so it doesn't get the
  // native focus trap / ESC handling that <dialog> gives us. Recreate both
  // here: ESC closes, Tab cycles within the banner's focusables.
  useEffect(() => {
    const banner = bottomBannerRef.current;
    const active =
      showOverlay && !overlayDismissed && variant === "bottom" && banner;
    if (!active) {
      return;
    }

    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Move focus into the banner (onto the container itself) without focusing
    // the email input, which would pop the mobile keyboard.
    banner.focus({ preventScroll: true });

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismissMethodRef.current = "esc";
        handleOverlayDismiss();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const focusables = Array.from(
        banner.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusables.length === 0) {
        event.preventDefault();
        banner.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables.at(-1);
      if (!(first && last)) {
        return;
      }
      const current = document.activeElement as HTMLElement | null;
      const insideBanner = current !== null && banner.contains(current);
      if (event.shiftKey) {
        if (!insideBanner || current === first || current === banner) {
          event.preventDefault();
          last.focus();
        }
      } else if (!insideBanner || current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showOverlay, overlayDismissed, variant, handleOverlayDismiss]);

  // Native <dialog> calls onClose for ESC without telling us why, so we
  // observe Escape keydowns while the center variant is open and tag the
  // dismiss method before the native close fires.
  useEffect(() => {
    if (variant !== "center") {
      return;
    }
    const isOpen = showOverlay && !overlayDismissed;
    if (!isOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismissMethodRef.current = "esc";
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showOverlay, overlayDismissed, variant]);

  // Fire upsell_offered exactly once per page visit, when the success screen
  // is actually showing the cross-sell (success + not yet subscribed to other
  // edition + not already accepted/skipped).
  useEffect(() => {
    if (!hasSubscribedOnce) {
      return;
    }
    if (subscribedToOther) {
      return;
    }
    if (upsellDone || upsellSkipped) {
      return;
    }
    if (upsellOfferedTrackedRef.current) {
      return;
    }
    upsellOfferedTrackedRef.current = true;
    upsellOfferedAtRef.current = Date.now();
    const surface: "overlay" | "inline" =
      showOverlay && !overlayDismissed ? "overlay" : "inline";
    analyticsRef.current.trackUpsellOffered({
      other_edition: otherEdition,
      surface,
    });
  }, [
    hasSubscribedOnce,
    subscribedToOther,
    upsellDone,
    upsellSkipped,
    showOverlay,
    overlayDismissed,
    otherEdition,
  ]);

  // Inline impression — fire once when the footer inline block enters the
  // viewport. Callback ref so we pick up the node whenever the conditional
  // `<section>` (re-)mounts, without juggling useEffect dependencies.
  const inlineSectionRef = useCallback((node: HTMLElement | null) => {
    inlineObserverRef.current?.disconnect();
    inlineObserverRef.current = null;
    if (!node || inlineViewedTrackedRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !inlineViewedTrackedRef.current) {
            inlineViewedTrackedRef.current = true;
            analyticsRef.current.trackInlineViewed();
            observer.disconnect();
            inlineObserverRef.current = null;
            break;
          }
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    inlineObserverRef.current = observer;
  }, []);

  if (subscribedToCurrent && !justSubscribed && !devForceActive) {
    return null;
  }

  const copy = EDITION_COPY[reportType];
  const crossSell = CROSS_SELL_COPY[reportType];

  // Surface-specific submit handlers so each rendering of the form attributes
  // its conversion to the right place. Without this, the email wall's three
  // sub-surfaces (bottom overlay, center modal, inline footer at end of
  // story) all roll up under a single source and we can't tell which one
  // converts. See use-newsletter-subscribe for the per-call source override.
  const submitWithSource = (source: string) => (event: React.FormEvent) => {
    event.preventDefault();
    submitAttemptedAtRef.current = Date.now();
    submitSucceededTrackedRef.current = false;
    submitFailedTrackedRef.current = false;
    analytics.trackSubmitAttempted();
    subscribe({ editions: [reportType], source });
  };

  const handleSubmitOverlayBottom = submitWithSource("email_wall_bottom");
  const handleSubmitOverlayCenter = submitWithSource("email_wall_center");
  const handleSubmitInlineFooter = submitWithSource("email_wall_inline");

  const handleUpsell = async () => {
    const surface: "overlay" | "inline" =
      showOverlay && !overlayDismissed ? "overlay" : "inline";
    analytics.trackUpsellAccepted({
      other_edition: otherEdition,
      surface,
      time_since_offered_ms:
        upsellOfferedAtRef.current !== null
          ? Date.now() - upsellOfferedAtRef.current
          : null,
    });
    setUpsellSubmitting(true);
    const ok = await subscribeToEdition({
      edition: otherEdition,
      includeEditions: ["engineer", "creative"],
    });
    setUpsellSubmitting(false);
    if (ok) {
      setUpsellDone(true);
    }
  };

  const overlayVisible = showOverlay && !overlayDismissed;
  const isBottomVariant = variant === "bottom";
  const showUpsell = !subscribedToOther;

  // Hide inline if overlay was triggered this session (dismissed or active)
  // — prevents the duplicated-yellow-surface problem
  const showInline = !(overlayWasTriggered || justSubscribed);
  const showInlineSuccess = justSubscribed && !overlayVisible;

  return (
    <>
      {/* ── Center modal (native <dialog> = ESC + focus trap for free) ── */}
      {!isBottomVariant && (
        <dialog
          aria-describedby="email-wall-center-desc"
          aria-labelledby="email-wall-center-title"
          className="m-auto w-[calc(100vw-2rem)] max-w-[520px] overflow-visible rounded-2xl border-none bg-transparent p-0 shadow-[var(--emailwall-shadow-overlay)] backdrop:bg-black/45 backdrop:backdrop-blur-[1px] open:animate-[gentleSlideUp_0.5s_cubic-bezier(0.16,1,0.3,1)]"
          onClick={(e) => {
            // Click on backdrop (the dialog element itself, not its content) closes
            if (e.target === e.currentTarget) {
              dismissMethodRef.current = "backdrop";
              handleOverlayDismiss();
            }
          }}
          onClose={handleOverlayDismiss}
          ref={dialogRef}
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          <div className="relative overflow-hidden rounded-2xl bg-[var(--explore-accent-yellow)]">
            <button
              aria-label="Close"
              className="absolute top-3 right-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--emailwall-on-accent-border)] text-[var(--explore-on-accent-text)] transition-colors hover:bg-[var(--explore-on-accent-text)]/20 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--explore-on-accent-text)]/25"
              onClick={() => {
                dismissMethodRef.current = "close_button";
                handleOverlayDismiss();
              }}
              type="button"
            >
              <X aria-hidden className="h-4 w-4" strokeWidth={2.25} />
            </button>
            <div className="px-6 py-8 sm:px-10 sm:py-10">
              <div className="mx-auto max-w-[440px]">
                <span className="sr-only" id="email-wall-center-title">
                  Subscribe to the AI Primer daily digest
                </span>
                <span className="sr-only" id="email-wall-center-desc">
                  Enter your email to receive the daily AI digest.
                </span>
                {!justSubscribed && (
                  <>
                    <div className="mb-5 flex justify-center">
                      <div className="h-14 w-14 overflow-hidden rounded-full shadow-[var(--emailwall-shadow-avatar)]">
                        <Image
                          alt="AI Primer"
                          className="h-full w-full object-cover"
                          height={56}
                          src="/logo.webp"
                          width={56}
                        />
                      </div>
                    </div>
                    <SubscribeForm
                      copy={copy}
                      email={email}
                      errorId="email-wall-center-error"
                      errorMessage={state.message}
                      isError={isError}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmitOverlayCenter}
                      setEmail={setEmail}
                      surface="overlay-center"
                    />
                  </>
                )}
                {justSubscribed && (
                  <SuccessState
                    crossSellCta={crossSell.cta}
                    crossSellPrompt={crossSell.prompt}
                    layout="center"
                    onSkip={handleOverlayUpsellSkip}
                    onUpsell={handleUpsell}
                    showUpsell={showUpsell}
                    upsellDone={upsellDone}
                    upsellSkipped={upsellSkipped}
                    upsellSubmitting={upsellSubmitting}
                  />
                )}
              </div>
            </div>
          </div>
        </dialog>
      )}

      {/* ── Bottom banner — form AND success render in the SAME surface ── */}
      {isBottomVariant && overlayVisible && (
        <div
          aria-describedby="email-wall-bottom-desc"
          aria-labelledby="email-wall-bottom-title"
          className="fixed right-0 bottom-0 left-0 z-[100] animate-[bottomSheetSlideUp_0.55s_cubic-bezier(0.22,1,0.36,1)] overflow-visible border-[var(--emailwall-on-accent-border)] border-t bg-[var(--explore-accent-yellow)] shadow-[var(--emailwall-shadow-banner)] outline-none"
          ref={bottomBannerRef}
          role="dialog"
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
          tabIndex={-1}
        >
          <button
            aria-label="Close"
            className="absolute top-1.5 right-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-full text-[var(--explore-on-accent-text)]/35 transition-[color,background-color] duration-150 hover:bg-[var(--explore-on-accent-text)]/10 hover:text-[var(--explore-on-accent-text)]/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--explore-on-accent-text)]/25 sm:top-2 sm:right-2"
            onClick={() => {
              dismissMethodRef.current = "close_button";
              handleOverlayDismiss();
            }}
            type="button"
          >
            <X aria-hidden className="h-2.5 w-2.5" strokeWidth={1.75} />
          </button>
          <div className="mx-auto flex max-w-[1280px] flex-col justify-center px-5 pt-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] sm:min-h-[214px] sm:px-10 sm:pt-6 sm:pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            <span className="sr-only" id="email-wall-bottom-title">
              Subscribe to the AI Primer daily digest
            </span>
            <span className="sr-only" id="email-wall-bottom-desc">
              Enter your email to receive the daily AI digest.
            </span>
            {!justSubscribed && (
              <SubscribeForm
                copy={copy}
                email={email}
                errorId="email-wall-bottom-error"
                errorMessage={state.message}
                isError={isError}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmitOverlayBottom}
                setEmail={setEmail}
                surface="overlay-bottom"
              />
            )}
            {justSubscribed && (
              <SuccessState
                crossSellCta={crossSell.cta}
                crossSellPrompt={crossSell.prompt}
                layout="horizontal"
                onSkip={handleOverlayUpsellSkip}
                onUpsell={handleUpsell}
                showUpsell={showUpsell}
                upsellDone={upsellDone}
                upsellSkipped={upsellSkipped}
                upsellSubmitting={upsellSubmitting}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Inline block at article footer ── */}
      {(showInline || showInlineSuccess) && (
        <section
          aria-label="Newsletter signup"
          className="relative mb-12 overflow-visible rounded-2xl"
          ref={inlineSectionRef}
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          <div className="overflow-hidden rounded-2xl bg-[var(--explore-accent-yellow)] px-6 py-12 sm:px-10 sm:py-14">
            <div className="mx-auto max-w-[480px]">
              {showInline && !showInlineSuccess && (
                <>
                  <div className="mb-7 flex justify-center">
                    <div className="h-16 w-16 overflow-hidden rounded-full shadow-[var(--emailwall-shadow-avatar)]">
                      <Image
                        alt="AI Primer"
                        className="h-full w-full object-cover"
                        height={64}
                        src="/logo.webp"
                        width={64}
                      />
                    </div>
                  </div>
                  <SubscribeForm
                    copy={copy}
                    email={email}
                    errorId="email-wall-inline-error"
                    errorMessage={state.message}
                    isError={isError}
                    isSubmitting={isSubmitting}
                    onSubmit={handleSubmitInlineFooter}
                    setEmail={setEmail}
                    surface="inline"
                  />
                </>
              )}

              {showInlineSuccess && (
                <SuccessState
                  crossSellCta={crossSell.cta}
                  crossSellPrompt={crossSell.prompt}
                  layout="center"
                  onSkip={() => {
                    analytics.trackUpsellSkipped({
                      other_edition: otherEdition,
                      surface: "inline",
                    });
                    setUpsellSkipped(true);
                  }}
                  onUpsell={handleUpsell}
                  showUpsell={showUpsell}
                  upsellDone={upsellDone}
                  upsellSkipped={upsellSkipped}
                  upsellSubmitting={upsellSubmitting}
                />
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
